import {
  CHECK_FUNCTIONS,
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { XStack, YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { pluralize } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

import {
  emptyPlaceholder as placeholderFor,
  FieldError,
  InlineField,
  PillField,
} from '../../field';
import {
  fieldErrorId,
  labelIdOf,
  useElementId,
  useFieldChecks,
} from '../../fieldIds';
import { Avatar } from '../../internals/Avatar';
import {
  addEntries,
  matchChoices,
  suggestionId,
  typedEntries,
  type ChipChoice,
} from '../../internals/chipEntries';
import { useGhostPills } from '../../internals/objectContext';
import {
  FIELD_ERROR_RING_CLASS,
  HELD_CLASS,
  PILL_BOX_CLASS,
  sizingClass,
  TEXT_STYLE,
  WRAP_GAP,
} from '../../tokens';

import { Chip, type ChipLook } from './Chip';
import { Suggestions } from './Suggestions';

import type { catalog } from '@littlebird/action-cards-view';

const LOOK: Record<'rows' | 'inline' | 'pills' | 'avatars', ChipLook> = {
  rows: 'chip',
  inline: 'text',
  pills: 'pill',
  avatars: 'chip',
};

const ADD_CHIP_CLASS: Record<ChipLook, string> = {
  chip: cn(
    TEXT_STYLE.meta,
    'h-[calc(24*var(--ac-u))] rounded-md border border-dashed border-border-strong px-[calc(8*var(--ac-u))] text-foreground-muted hover:border-border-strongest hover:text-foreground'
  ),
  pill: cn(
    PILL_BOX_CLASS,
    'rounded-full border border-dashed border-border-strong font-normal text-foreground-muted hover:border-border-strongest hover:text-foreground'
  ),
  text: cn(
    TEXT_STYLE.meta,
    'h-[calc(20*var(--ac-u))] text-foreground-muted hover:text-foreground'
  ),
};

// The input keeps the add pill's box, so an empty list never goes blank while
// typing; it starts at the add pill's width and grows with the text
const EDITING_PILL_CLASS = cn(
  PILL_BOX_CLASS,
  'inline-flex min-w-[calc(112*var(--ac-u))] max-w-full shrink-0 items-center rounded-full border border-border-strongest font-normal'
);

const INVALID_ADDRESS = 'Not a valid address';

const REVEAL_LINK_CLASS =
  'self-start text-[length:var(--ac-meta)] leading-[calc(20*var(--ac-u))] text-foreground-muted underline-offset-2 transition-colors duration-[120ms] hover:text-foreground hover:underline';

// A list of entries drawn as chips. `items` gives each value its display label, avatar
// or colour; `suggestions` offer a typeahead while free text is allowed; `options`
// close the list to a fixed set. Enter or a comma adds, Backspace on an empty input
// removes the last chip. With `revealLabel`, an empty unfocused row is only a link
export const ChipList: ComponentFn<typeof catalog, 'ChipList'> = ({
  props,
  children,
  bindings,
}) => {
  const id = useElementId(props);
  const path = bindings?.value;
  const [value, setValue] = useBoundProp<string[]>(props.value, path);
  const checks = useFieldChecks({
    path,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [opened, setOpened] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [added, setAdded] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const held = status === 'running';
  // A literal value has no state path to write to, so a chip added to it would be lost
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const entries = value ?? [];
  const error = checks.error ?? props.error;
  const look = LOOK[props.appearance ?? 'rows'];
  const ghostPills = useGhostPills();
  const closedList = props.options !== undefined;
  const entryProblem = (entry: string): string | undefined => {
    const failed = props.checks?.find(
      (check) =>
        (check.type === 'everyEmail' || check.type === 'everyPattern') &&
        !CHECK_FUNCTIONS[check.type]([entry], check.args)
    );
    if (!failed) return undefined;
    return failed.type === 'everyEmail' ? INVALID_ADDRESS : failed.message;
  };
  // A problem its chips already mark is not repeated for the row
  const rowError =
    error && !entries.some((entry) => entryProblem(entry) !== undefined);
  const known = [
    ...(props.items ?? []),
    ...(props.options ?? []),
    ...(props.suggestions ?? []),
  ];
  const choiceFor = (entry: string): ChipChoice =>
    known.find((choice) => choice.value === entry) ?? {
      value: entry,
      label: entry,
    };
  const matches = focused
    ? matchChoices(props.options ?? props.suggestions ?? [], draft, entries)
    : [];
  // A closed list always has a pick ready; an open one adds the typed text until
  // the arrow keys choose a suggestion
  const picked = matches[closedList ? Math.max(highlighted, 0) : highlighted];

  const write = (next: string[]) => {
    setValue(next);
    checks.afterChange();
    controls.commitNow();
  };

  const add = (additions: string[]) => {
    setDraft('');
    setHighlighted(-1);
    const next = addEntries(entries, additions);
    if (next.length === entries.length) return;
    setAdded([...added, ...additions]);
    write(next);
  };

  const commitDraft = () => {
    if (picked) add([picked.value]);
    else if (!closedList) add(typedEntries(draft));
  };

  // Leaving keeps typed free text but never picks an option on the user's behalf
  const leaveDraft = () => {
    if (closedList) setDraft('');
    else add(typedEntries(draft));
  };

  const openInput = () => {
    setOpened(true);
    inputRef.current?.focus();
  };

  // The pressed remove button leaves with its chip, so focus moves to the input
  const remove = (index: number) => {
    write(entries.filter((_, position) => position !== index));
    openInput();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'ArrowDown' && matches.length > 0) {
      event.preventDefault();
      setHighlighted((highlighted + 1) % matches.length);
    } else if (event.key === 'ArrowUp' && matches.length > 0) {
      event.preventDefault();
      setHighlighted(highlighted <= 0 ? matches.length - 1 : highlighted - 1);
    } else if (event.key === 'Backspace' && draft === '' && entries.length) {
      event.preventDefault();
      write(entries.slice(0, -1));
    }
  };

  const empty = entries.length === 0;
  if (props.revealLabel && empty && !editable) return null;
  // Inside an object a revealable empty list is a ghost add pill, not a text link
  const ghost =
    props.revealLabel !== undefined && empty && ghostPills && editable;
  if (props.revealLabel && empty && !focused && !opened && !ghost) {
    return (
      <button
        type="button"
        className={cn(REVEAL_LINK_CLASS, sizingClass(props.sizing))}
        onClick={() => setOpened(true)}
      >
        {props.revealLabel}
      </button>
    );
  }

  const listId = `${id}-suggestions`;
  const emptyPlaceholder = placeholderFor(
    props.placeholder || props.addLabel,
    props.label,
    'Add'
  );
  const addLabel = props.addLabel ?? (ghost ? props.label : undefined);
  const showAddChip =
    editable && addLabel && !focused && !opened && draft === '';

  let entryControl: ReactNode = null;
  if (showAddChip) {
    entryControl = (
      <button
        type="button"
        // Sized by its own words, never a fixed width, so a long add label stays
        // inside its pill and never overlaps the next one
        className={cn(
          'inline-flex min-w-0 max-w-full shrink items-center gap-[var(--ac-gap-xs)] whitespace-nowrap transition-colors duration-[120ms]',
          ADD_CHIP_CLASS[ghost ? 'pill' : look]
        )}
        // Opens on press: a field committing on blur can move this chip before the click arrives
        onPointerDown={(event) => {
          if (event.button !== 0 || event.ctrlKey) return;
          event.preventDefault();
          openInput();
        }}
        onClick={openInput}
      >
        <span className="truncate">{addLabel}</span>
      </button>
    );
  } else if (editable) {
    const input = (
      <input
        ref={inputRef}
        id={id}
        // The reveal link and the add chip mount the input and hand it focus
        autoFocus={opened}
        autoComplete="off"
        role={matches.length > 0 ? 'combobox' : undefined}
        aria-expanded={matches.length > 0 ? true : undefined}
        aria-controls={matches.length > 0 ? listId : undefined}
        aria-activedescendant={
          picked ? suggestionId(listId, matches.indexOf(picked)) : undefined
        }
        aria-label={props.label ? undefined : props.addLabel}
        value={draft}
        placeholder={
          empty ? emptyPlaceholder : look === 'pill' ? 'Add' : undefined
        }
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? fieldErrorId(id) : undefined}
        className={cn(
          'bg-transparent text-foreground outline-none placeholder:text-foreground-muted',
          look === 'pill'
            ? 'h-full w-full min-w-0 text-[length:var(--ac-pill)] [field-sizing:content]'
            : look === 'text'
              ? // A one-line preview leaves no gap after the last name; typing widens it
                'h-[calc(24*var(--ac-u))] min-w-px max-w-full flex-none text-[length:var(--ac-body)] leading-[calc(24*var(--ac-u))] [field-sizing:content]'
              : // Starts at the add chip's width and grows with the text
                cn(
                  'h-[calc(24*var(--ac-u))] text-[length:var(--ac-body)] leading-[calc(24*var(--ac-u))]',
                  'min-w-[calc(112*var(--ac-u))] max-w-full flex-none [field-sizing:content]'
                )
        )}
        onChange={(event) => {
          setDraft(event.target.value);
          setHighlighted(-1);
        }}
        onFocus={() => {
          setFocused(true);
          if (path) controls.focus(path);
        }}
        onBlur={() => {
          leaveDraft();
          setFocused(false);
          setOpened(false);
          checks.onBlur();
          if (path) controls.blur(path);
        }}
        onKeyDown={onKeyDown}
      />
    );
    entryControl =
      look === 'pill' ? (
        <span className={EDITING_PILL_CLASS}>{input}</span>
      ) : (
        input
      );
  }

  // A locked row keeps its input's footprint; the one-line look's input takes
  // none, so it has nothing to hold
  if (look !== 'text' && !editable && (held || session.readOnly))
    entryControl = (
      <span
        aria-hidden="true"
        className="h-[calc(24*var(--ac-u))] w-[calc(112*var(--ac-u))] shrink-0"
      />
    );

  const chips = entries.map((entry, index) => (
    <Chip
      key={`${index}-${entry}`}
      choice={choiceFor(entry)}
      look={look}
      added={added.includes(entry)}
      invalidMessage={entryProblem(entry)}
      onRemove={editable ? () => remove(index) : undefined}
    />
  ));

  if (editable) {
    entryControl = (
      <Popover.Root open={matches.length > 0}>
        <Popover.Anchor asChild>{entryControl}</Popover.Anchor>
        {matches.length > 0 ? (
          <Popover.Content
            align="start"
            sideOffset={4}
            className="w-max max-w-80 p-0"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            <Suggestions
              id={listId}
              choices={matches}
              highlighted={picked ? matches.indexOf(picked) : -1}
              onPick={(choice) => add([choice.value])}
            />
          </Popover.Content>
        ) : null}
      </Popover.Root>
    );
  }

  if (props.appearance === 'avatars') {
    return (
      <div
        className="flex min-w-0 items-center gap-[var(--ac-gap-sm)]"
        aria-label={entries.map((entry) => choiceFor(entry).label).join(', ')}
      >
        <div className="flex space-x-[calc(-6*var(--ac-u))]" aria-hidden="true">
          {entries.slice(0, 4).map((entry) => {
            const choice = choiceFor(entry);
            return (
              <Avatar
                key={entry}
                id={entry}
                name={choice.label}
                avatarUrl={choice.avatar_url}
                size={24}
                shape="circle"
              />
            );
          })}
        </div>
        <span className={cn(TEXT_STYLE.meta, 'text-foreground-muted')}>
          {entries.length} {pluralize(entries.length, 'guest')}
        </span>
      </div>
    );
  }

  if (look === 'text') {
    return (
      <YStack
        className={cn(
          'min-w-0 gap-[var(--ac-gap-xs)]',
          sizingClass(props.sizing)
        )}
      >
        <XStack
          wrap
          aria-labelledby={!editable && props.label ? labelIdOf(id) : undefined}
          className={cn(
            'min-w-0 items-center gap-x-[var(--ac-gap-sm)] gap-y-[var(--ac-gap-xs)]',
            TEXT_STYLE.meta,
            rowError && FIELD_ERROR_RING_CLASS,
            held && HELD_CLASS
          )}
        >
          {props.label ? (
            <label
              id={labelIdOf(id)}
              htmlFor={editable ? id : undefined}
              className={cn(
                'text-foreground-muted',
                props.labelHidden && 'sr-only'
              )}
            >
              {props.label}
            </label>
          ) : null}
          {chips}
          {entryControl}
          {children}
        </XStack>
        <FieldError id={id} error={error} />
      </YStack>
    );
  }

  // A pill list sits among other pills, so it carries no visible field label
  if (look === 'pill') {
    return (
      <PillField id={id} error={error} className={sizingClass(props.sizing)}>
        {props.label ? (
          <label
            id={labelIdOf(id)}
            htmlFor={editable ? id : undefined}
            className="sr-only"
          >
            {props.label}
          </label>
        ) : null}
        <XStack
          wrap
          aria-labelledby={!editable && props.label ? labelIdOf(id) : undefined}
          className={cn(
            'min-w-0 items-center',
            WRAP_GAP.sm,
            rowError && FIELD_ERROR_RING_CLASS,
            held && HELD_CLASS
          )}
        >
          {chips}
          {entryControl}
        </XStack>
      </PillField>
    );
  }

  const field = (
    <InlineField
      id={id}
      label={props.label}
      labelHidden={props.labelHidden}
      labelsControl={editable && !showAddChip}
      error={error}
      recipient={props.recipient === true}
    >
      <XStack
        wrap
        data-recipient-chips={props.recipient === true || undefined}
        aria-labelledby={!editable && props.label ? labelIdOf(id) : undefined}
        className={cn(
          'mx-[calc(-6*var(--ac-u))] min-h-[var(--ac-field-lh)] min-w-0 items-center gap-x-[calc(6*var(--ac-u))] gap-y-[var(--ac-gap-xs)] rounded-md px-[calc(6*var(--ac-u))] py-[calc(2*var(--ac-u))]',
          editable &&
            'transition-colors duration-[120ms] focus-within:ring-1 focus-within:ring-border-strongest hover:bg-surface-hover motion-reduce:duration-[60ms]',
          props.recipient === true &&
            'gap-[var(--ac-gap-xs)] py-[var(--ac-gap-xs)]',
          rowError && FIELD_ERROR_RING_CLASS,
          held && HELD_CLASS
        )}
      >
        {chips}
        {entryControl}
        {children}
      </XStack>
    </InlineField>
  );

  return props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
